import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {
  AangeverBurgerlijkeStaat,
  AangeverSamenwoonSituatie,
  BokkzIbDataService,
  IncomeTaxEntity,
  Periode,
  Relatiegegevens,
  Woonland
} from '@snelstart-ventures/fiscaal/domain-logic';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {filter, Observable, shareReplay, take, tap} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiTooltipModule} from '@snelstart/ui-tooltip';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {
  optionsLandcodesMetNLD,
  optionsLandcodesZonderNLD
} from '../../gedeelde-componenten/gedeelde-componenten.component';
import {SharedUiSearchControlModule} from '@snelstart/ui-search-control';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-aangever',
  templateUrl: './aangever.component.html',
  styleUrls: ['./aangever.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiTooltipModule,
    CommonModule,
    SharedUiAlertModule,
    SharedUiSearchControlModule,
  ],
})
export class AangeverComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(BokkzIbDataService);
  protected readonly AangeverBurgerlijkeStaat = AangeverBurgerlijkeStaat;

  public aangeverFg = new FormGroup({
    voorletters: new FormControl<string>(''.trim(), {
      nonNullable: true,
      validators: [Validators.minLength(1), Validators.maxLength(20)],
    }),
    roepnaam: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [Validators.minLength(1), Validators.maxLength(200)],
    }),
    tussenvoegsels: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [Validators.minLength(1), Validators.maxLength(10)],
    }),
    achternaam: new FormControl<string>(''.trim(), {
      nonNullable: true,
      validators: [Validators.minLength(1), Validators.maxLength(200)],
    }),
    geboortedatum: new FormControl<Date | null>(null),
    overlijdensdatum: new FormControl<Date | null>(null),
    bsn: new FormControl<string>(''.trim(), {
      nonNullable: true,
      validators: [Validators.pattern(/^\d{9}$/)],
    }),
    burgerlijkeStaat: new FormControl<AangeverBurgerlijkeStaat>(AangeverBurgerlijkeStaat.ALLEENSTAAND, {
      nonNullable: true,
    }),
    samenwoonSituatie: new FormControl<AangeverSamenwoonSituatie | null>(null),
    relatiegegevens: new FormControl<Relatiegegevens[] | null>(null),
    landCodeNationaliteit: new FormControl<string | null>(null),
  });

  public gezamenlijkeAangifteFg = new FormGroup({
    gezamenlijkeAangifte: new FormControl<boolean | null>(false),
  });

  public woonlandFg = new FormGroup({
    inwonerLandNonDiscriminatie: new FormControl<boolean | null>(false),
    woonlandVoldoetVoorKwalificatie: new FormControl<boolean | null>(false),
    belastingplichtigeVoldoetAanVoorwaardenKwalificatie: new FormControl<boolean | null>(false),
    landCodeWoonland: new FormControl<string | null>(null),
    periodeWoonachtigInWoonland: new FormGroup({
      begindatum: new FormControl<Date | undefined>(undefined),
      einddatum: new FormControl<Date | undefined>(undefined),
    }),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
    take(1),
    filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
    takeUntilDestroyed(this.destroyRef),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  public optionsJaNee: SelectFormControlItem<boolean | undefined>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'},
  ];

  public optionsLandcodesMetNld: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;

  public optionsLandcodesZonderNld: SelectFormControlItem<string>[] = optionsLandcodesZonderNLD;

  public optiesBurgerlijkeStaat: SelectFormControlItem<AangeverBurgerlijkeStaat | null>[] = [
    {
      value: AangeverBurgerlijkeStaat.ALLEENSTAAND,
      label: 'Hele jaar alleenstaand',
    },
    {
      value: AangeverBurgerlijkeStaat.GEHUWD,
      label: 'Hele jaar gehuwd',
    },
    {
      value: AangeverBurgerlijkeStaat.SAMENWONEND,
      label: 'Hele jaar samenwonend',
    },
    {
      value: AangeverBurgerlijkeStaat.SITUATIE_GEWIJZIGD,
      label: `Gewijzigd/deels samenwonend`,
    },
  ];

  public optiesSamenwonend: SelectFormControlItem<AangeverSamenwoonSituatie | null>[] = [
    {
      value: AangeverSamenwoonSituatie.NOTARIEEL_SAMENLEVINGS_CONTRACT,
      label: 'Samen een notarieel samenlevingscontract',
    },
    {
      value: AangeverSamenwoonSituatie.SAMEN_KIND,
      label: 'Samen een kind',
    },
    {
      value: AangeverSamenwoonSituatie.KIND_ANDER_ERKEND,
      label: 'De ene heeft kind van ander erkend',
    },
    {
      value: AangeverSamenwoonSituatie.SAMEN_EIGENAAR_WONING,
      label: 'Samen eigenaar woning (hoofdverblijf)',
    },
    {
      value: AangeverSamenwoonSituatie.MINDERJARIG_KIND_OP_WOONADRES,
      label: 'Minderjarig kind op woonadres',
    },
    {
      value: AangeverSamenwoonSituatie.VORIG_JAAR_FISCAAL_PARTNER,
      label: 'Samen vorig (aangifte)jaar fiscaal partners',
    },
    {
      value: AangeverSamenwoonSituatie.PARTNER_PENSIOENREGELING,
      label: 'Partners in een pensioenregeling',
    },
    {
      value: AangeverSamenwoonSituatie.GEEN,
      label: 'Geen van voorgaande situaties',
    },
  ];

  ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data) => {
      if (data.aangifte) {
        this.aangeverFg.patchValue(data.aangifte.aangever, {emitEvent: false});
        this.gezamenlijkeAangifteFg.patchValue({gezamenlijkeAangifte: data.aangifte.gezamenlijkeAangifte}, {emitEvent: false});
        if (data.aangifte.biljetSoort === 'C') {
          if (data.aangifte.buitenlandseBelastingplicht?.woonlandGegevens?.at(0)) {
            this.patchWoonlandGegevens(String(data.year), data);
          }
          if (data.aangifte.partnerAangifte?.buitenlandseBelastingplicht?.woonlandGegevens?.at(0)) {
            this.patchWoonlandGegevens(String(data.year), data);
          }
        }
      }
    });
    this.aangeverFg.valueChanges.pipe(
      tap(() => this.saveForm())
    ).subscribe();
    this.woonlandFg.valueChanges.pipe(
      tap(() => this.saveForm())
    ).subscribe();
    this.gezamenlijkeAangifteFg.valueChanges.pipe(
      tap(() => this.saveForm())
    ).subscribe();
  }

  ngOnDestroy(): void {
    // rely on saveFrom in component
  }

  public patchWoonlandGegevens(year: string, incomeTaxEntity: IncomeTaxEntity) {
    const buitenlandseBelastingplicht = incomeTaxEntity.aangifte?.buitenlandseBelastingplicht;
    const woonland = buitenlandseBelastingplicht?.woonlandGegevens[0] as Woonland;

    const woonland1 = {
      landCodeWoonland: woonland.landCodeWoonland ?? '', // ensure string, never null
      periodeWoonachtigInWoonland: {
        begindatum: new Date(`${year}-01-01`),
        einddatum: new Date(`${year}-12-31`),
      } as Periode,
    } as Woonland;

    this.woonlandFg.patchValue(woonland1);

    const flags = this.berekenWoonlandFlags(woonland1.landCodeWoonland);
    this.woonlandFg.patchValue(flags);

    if (buitenlandseBelastingplicht?.belastingplichtigeVoldoetAanVoorwaardenKwalificatie !== undefined) {
      this.woonlandFg.patchValue({
        belastingplichtigeVoldoetAanVoorwaardenKwalificatie:
        buitenlandseBelastingplicht.belastingplichtigeVoldoetAanVoorwaardenKwalificatie,
      });
    }
  }

  private berekenWoonlandFlags(landCode: string): {
    inwonerLandNonDiscriminatie: boolean;
    woonlandVoldoetVoorKwalificatie: boolean;
  } {
    return {
      inwonerLandNonDiscriminatie: ['BEL', 'SUR', 'AWB'].includes(landCode),
      woonlandVoldoetVoorKwalificatie: [
        'ALA',
        'ABW',
        'BEL',
        'BES',
        'BGR',
        'CYP',
        'PRT',
        'DNK',
        'DEU',
        'EST',
        'FIN',
        'FRA',
        'GUF',
        'GRC',
        'GLP',
        'HUN',
        'IRL',
        'ITA',
        'HRV',
        'LVA',
        'LTU',
        'LIE',
        'LUX',
        'MLT',
        'MTQ',
        'MYT',
        'NOR',
        'AUT',
        'POL',
        'PRT',
        'REU',
        'ROU',
        'MAF',
        'SVN',
        'SVK',
        'ESP',
        'SUR',
        'CZE',
        'SWE',
        'CHE',
      ].includes(landCode),
    };
  }

  public saveForm() {
    {
      if (this.aangeverFg.invalid) {
        this.aangeverFg.markAllAsTouched();
      }

      if (this.woonlandFg.invalid) {
        this.woonlandFg.markAllAsTouched();
      }

      this.aangeverFg.updateValueAndValidity({emitEvent: false});
      this.gezamenlijkeAangifteFg.updateValueAndValidity({emitEvent: false});
      this.woonlandFg.updateValueAndValidity({emitEvent: false});

      const aangever = this.stripEmptyStrings(this.aangeverFg.getRawValue());
      aangever.tussenvoegsels = aangever.tussenvoegsels?.trim() ? aangever.tussenvoegsels : null;
      if (aangever.burgerlijkeStaat === AangeverBurgerlijkeStaat.GEHUWD) {
        aangever.samenwoonSituatie = null;
        aangever.relatiegegevens = null;
      }
      if (aangever.burgerlijkeStaat === AangeverBurgerlijkeStaat.SAMENWONEND) {
        aangever.relatiegegevens = null;
      }
      if (aangever.burgerlijkeStaat === AangeverBurgerlijkeStaat.SITUATIE_GEWIJZIGD) {
        aangever.samenwoonSituatie = null;
      }
      if (aangever.burgerlijkeStaat === AangeverBurgerlijkeStaat.ALLEENSTAAND) {
        aangever.samenwoonSituatie = null;
        aangever.relatiegegevens = null;
      }

      const woonland = this.woonlandFg.getRawValue();
      const woonlandForSave = {
        ...woonland,
        landCodeWoonland: woonland.landCodeWoonland ?? '',
        periodeWoonachtigInWoonland: {
          begindatum: woonland.periodeWoonachtigInWoonland?.begindatum ?? undefined,
          einddatum: woonland.periodeWoonachtigInWoonland?.einddatum ?? undefined,
        },
      };

      this.dataService.patchIncomeTaxEntity((currentEntity) => {
        const biljetSoort = currentEntity.aangifte?.biljetSoort ?? 'P';
        const aangifte: any = {
          ...currentEntity.aangifte,
          biljetSoort,
          aangifteJaar: currentEntity.aangifte?.aangifteJaar ?? currentEntity.year,
          aangever: aangever,
        }
        if (aangever.burgerlijkeStaat === AangeverBurgerlijkeStaat.ALLEENSTAAND) {
          aangifte.partnerAangifte = undefined;
          aangifte.gezamenlijkeAangifte = false;
        }
        if (aangever.samenwoonSituatie === AangeverSamenwoonSituatie.GEEN) {
          aangifte.partnerAangifte = undefined;
          aangifte.gezamenlijkeAangifte = false;
        }
        if (biljetSoort === 'C') {
          aangifte.buitenlandseBelastingplicht = {
            ...currentEntity.aangifte?.buitenlandseBelastingplicht,
            inwonerLandNonDiscriminatie: this.berekenWoonlandFlags(woonlandForSave.landCodeWoonland)
              .inwonerLandNonDiscriminatie,
            woonlandVoldoetVoorKwalificatie: this.berekenWoonlandFlags(woonlandForSave.landCodeWoonland)
              .woonlandVoldoetVoorKwalificatie,
            belastingplichtigeVoldoetAanVoorwaardenKwalificatie:
              woonlandForSave.belastingplichtigeVoldoetAanVoorwaardenKwalificatie ?? false,
            woonlandGegevens: [woonlandForSave],
          };
        } else {
          // Ensure property is removed if present
          delete aangifte.buitenlandseBelastingplicht;
        }
        return {
          ...currentEntity,
          aangifte,
        };
      });
    }
  }

  private stripEmptyStrings<T extends object>(obj: T): T {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, value === '' ? undefined : value])) as T;
  }

}
